import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, PageHeader } from "@/components/common";
import ReportFilters, {
  defaultDateRange, formatSec, ReportPagination, REPORT_PAGE_SIZE,
} from "@/components/reports/ReportFilters";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Clock } from "@phosphor-icons/react";

export default function TimesheetReport() {
  const { user } = useAuth();
  const accountTz = user?.timezone || "UTC";
  const defaults = defaultDateRange(1, accountTz);
  const [dateFrom, setDateFrom] = useState(defaults.dateFrom);
  const [dateTo, setDateTo] = useState(defaults.dateTo);
  const [agentId, setAgentId] = useState("");
  const [applied, setApplied] = useState({ ...defaults, agentId: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const tz = data?.timezone || accountTz;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        date_from: applied.dateFrom,
        date_to: applied.dateTo,
        page,
        page_size: REPORT_PAGE_SIZE,
      };
      if (applied.agentId) params.agent_id = applied.agentId;
      const { data: res } = await api.get("/reports/timesheet", { params });
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [applied, page]);

  useEffect(() => { load(); }, [load]);

  const selected = (data?.agents || [])[0];
  const dayChart = (selected?.by_day || []).map((d) => ({
    date: d.date.slice(5),
    calls: d.calls,
    talk_min: Math.round((d.talk_sec || 0) / 60),
    pause_min: Math.round((d.pause_sec || 0) / 60),
  }));
  const rowsTotal = data?.rows_total ?? (data?.agents || []).length;

  return (
    <div data-testid="timesheet-report">
      <PageHeader
        title="Timesheet"
        subtitle={`Agent calls, talk time, and pause time by day (${tz}).`}
      />

      <ReportFilters
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFrom={setDateFrom}
        onDateTo={setDateTo}
        agentId={agentId}
        onAgentId={setAgentId}
        agents={data?.agent_options || []}
        onApply={() => {
          setPage(1);
          setApplied({ dateFrom, dateTo, agentId });
        }}
        onClear={() => {
          const d = defaultDateRange(1, accountTz);
          setDateFrom(d.dateFrom); setDateTo(d.dateTo); setAgentId("");
          setPage(1);
          setApplied({ ...d, agentId: "" });
        }}
        applying={loading}
        testid="timesheet-filters"
        timezone={tz}
      />

      {loading && !data ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !data || !(data.agents || []).length ? (
        <EmptyState
          icon={Clock}
          title="No timesheet data"
          description="Agents with activity in this range will appear here."
          testid="timesheet-empty"
        />
      ) : (
        <>
          {selected ? (
            <div className="rounded-xl border border-border bg-card p-4 h-72 mb-6">
              <p className="text-sm font-semibold mb-2">
                Daily activity — {selected.agent_name}
              </p>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={dayChart}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="calls" fill="#4f46e5" name="Calls" />
                  <Bar dataKey="talk_min" fill="#10b981" name="Talk (min)" />
                  <Bar dataKey="pause_min" fill="#f59e0b" name="Pause (min)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : null}

          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Agent</TableHead>
                  <TableHead className="text-right">Calls</TableHead>
                  <TableHead className="text-right">Talk time</TableHead>
                  <TableHead className="text-right">Pause time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data.agents || []).map((a) => (
                  <TableRow key={a.agent_id} data-testid={`timesheet-row-${a.agent_id}`}>
                    <TableCell className="font-medium text-sm">{a.agent_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{a.total_calls}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatSec(a.talk_sec)}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatSec(a.pause_sec)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <ReportPagination
              page={data.page || page}
              pageSize={data.page_size || REPORT_PAGE_SIZE}
              total={rowsTotal}
              onPage={setPage}
              testid="timesheet-pagination"
            />
          </div>
        </>
      )}
    </div>
  );
}
